import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { IconAlert, IconCheck, IconPlus, IconSearch, IconUsers } from '../components/icons';
import { RowActions } from '../components/RowActions';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, StatCard, Toggle, useAsync, useToast } from '../components/ui';
import { api, request } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '../lib/format';

const STATUS: Record<string, { label: string; variant: string }> = {
  draft: { label: 'Entwurf', variant: '' },
  pending_approval: { label: 'Wartet auf Freigabe', variant: 'warning' },
  approved: { label: 'Freigegeben', variant: 'info' },
  rejected: { label: 'Abgelehnt', variant: 'danger' },
  commissioned: { label: 'Beauftragt', variant: 'primary' },
  completed: { label: 'Abgeschlossen', variant: 'success' },
  cancelled: { label: 'Storniert', variant: '' },
};

/** Gilt der Versicherungsnachweis noch? */
function insuranceState(datum: string | null): 'ok' | 'expired' | 'unknown' {
  if (!datum) return 'unknown';
  return datum >= new Date().toISOString().slice(0, 10) ? 'ok' : 'expired';
}

/**
 * Fremdvergabe (Kap. 11).
 *
 * Der Ablauf folgt dem der Beschaffung, weil er dieselbe Frage beantwortet:
 * Wer darf in wessen Namen Geld ausgeben? Ab einer Wertgrenze wandert die
 * Vergabe in die Freigabe, darunter geht sie direkt hinaus.
 *
 * Anders als eine Bestellung braucht sie den Versicherungsnachweis des
 * Dienstleisters – bei einer Leistung auf fremdem Grundstück haftet sonst
 * der Auftraggeber mit.
 */
export function ContractingPage({ view = 'vergaben' }: { view?: 'vergaben' | 'dienstleister' }) {
  const [searchParams] = useSearchParams();
  // Vergaben und Dienstleister sind zwei Seiten (`/contracting`, `/contractors`).
  // Der frühere Reiter `?tab=dienstleister` leitet auf die neue Adresse um.
  // Die Weiterleitung steht in einer Hülle, weil sie vor den Hooks der Seite
  // stehen müsste – und ein Hook darf nicht hinter einem frühen `return` folgen.
  if (view === 'vergaben' && searchParams.get('tab') === 'dienstleister') return <Navigate to="/contractors" replace />;
  return <ContractingView view={view} />;
}

function ContractingView({ view }: { view: 'vergaben' | 'dienstleister' }) {
  const { can } = useAuth();
  const { notify } = useToast();
  const tab = view;
  const [modal, setModal] = useState<string | null>(null);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showInactive, setShowInactive] = useState(false);

  // Die Vergabeliste braucht nur die Vergabeseite.
  const orders = useAsync(
    () => (tab === 'vergaben' ? api.get('/api/contracting/orders', filters) : Promise.resolve(null)),
    [tab, JSON.stringify(filters)],
  );
  const contractors = useAsync(
    () => api.get('/api/contracting/contractors', { includeInactive: showInactive ? 'true' : '' }),
    [showInactive],
  );

  const mayRequest = can('contracting.request');
  const mayApprove = can('contracting.approve');
  const mayCommission = can('contracting.commission');
  const mayContractors = can('contracting.contractors');

  // Kennzahlen der Dienstleisterseite – gezählt werden nur aktive Einträge,
  // auch wenn „Stillgelegte anzeigen“ die Liste verlängert.
  const aktive: any[] = (contractors.data ?? []).filter((entry: any) => entry.is_active);
  const nachweisProblem = aktive.filter((entry) => insuranceState(entry.insurance_valid_until) !== 'ok');
  const inDreissigTagen = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
  const nachweisBald = aktive.filter(
    (entry) => insuranceState(entry.insurance_valid_until) === 'ok' && entry.insurance_valid_until <= inDreissigTagen,
  );
  const bewertet = aktive.filter((entry) => entry.avg_rating != null);
  const bewertung = bewertet.length ? bewertet.reduce((sum, entry) => sum + Number(entry.avg_rating), 0) / bewertet.length : 0;

  const items: any[] = orders.data?.items ?? [];
  const summary = orders.data?.summary ?? {};
  const threshold = orders.data?.threshold ?? 0;

  const act = async (order: any, aktion: string, body?: unknown, erfolg?: string) => {
    try {
      const antwort: any = await api.post(`/api/contracting/orders/${order.id}/${aktion}`, body);

      // Beim Beauftragen zählt nicht nur, dass es geklappt hat, sondern ob
      // das Schreiben hinausging. Ein Dienstleister ohne hinterlegte Adresse
      // ist kein Fehler – aber wer es erst beim Nachfragen erfährt, hat den
      // Auftrag längst als erledigt abgehakt.
      const versand = antwort?.dispatch;
      if (versand?.mailId) {
        notify(`${erfolg ?? 'Gespeichert.'} Beauftragung an ${versand.recipients.join(', ')} versendet.`, 'success');
      } else if (versand) {
        notify(`${erfolg ?? 'Gespeichert.'} Schreiben erzeugt, aber nicht versendet: ${versand.reason}`, 'info');
      } else {
        notify(erfolg ?? 'Gespeichert.', 'success');
      }

      orders.reload();
      contractors.reload();
      setModal(null);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    }
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconUsers width={22} height={22} />}
        title={tab === 'vergaben' ? 'Auftragsvergabe' : 'Dienstleister'}
        subtitle={
          tab === 'vergaben' ? (
            <>
              Beauftragung externer Dienstleister mit Freigabe ab {formatCurrency(threshold)}. Vor der Beauftragung wird
              geprüft, ob der Versicherungsnachweis noch gilt.
            </>
          ) : (
            <>Externe Dienstleister mit Gewerken, Stundensätzen, Rahmenverträgen und dem Haftpflichtnachweis, der vor jeder Beauftragung gilt.</>
          )
        }
        pill={tab === 'vergaben' ? 'Fremdleistung' : 'Geschäftspartner'}
        tone="accent"
        actions={
          <>
            {tab === 'vergaben' && mayRequest && (
              <button className="btn primary" onClick={() => setModal('neu')}>
                <IconPlus width={15} height={15} /> Vergabe beantragen
              </button>
            )}
            {tab === 'dienstleister' && mayContractors && (
              <button className="btn primary" onClick={() => setModal('dienstleister-neu')}>
                <IconPlus width={15} height={15} /> Dienstleister anlegen
              </button>
            )}
          </>
        }
      />

      {tab === 'vergaben' ? (
        <div className="grid cols-4">
          <StatCard
            label="Wartet auf Freigabe"
            value={summary.wartend ?? 0}
            tone={(summary.wartend ?? 0) > 0 ? 'warning' : 'success'}
            emphasis={(summary.wartend ?? 0) > 0 ? 'attention' : undefined}
            icon={<IconAlert width={17} height={17} />}
            hint={mayApprove ? 'Ihre Entscheidung steht aus' : 'bei der Freigabe'}
          />
          <StatCard
            label="Freigegeben"
            value={summary.freigegeben ?? 0}
            tone="info"
            icon={<IconCheck width={17} height={17} />}
            hint="noch nicht beauftragt"
          />
          <StatCard label="Beauftragt" value={summary.beauftragt ?? 0} tone="primary" hint="in Ausführung" />
          <StatCard
            label="Vergabevolumen"
            value={formatCurrency(summary.volumen ?? 0)}
            tone="success"
            hint="beauftragt und abgeschlossen"
          />
        </div>
      ) : (
        <div className="grid cols-4">
          <StatCard
            label="Aktive Dienstleister"
            value={aktive.length}
            tone="primary"
            icon={<IconUsers width={17} height={17} />}
            hint={`${aktive.filter((entry: any) => entry.framework_contract).length} mit Rahmenvertrag`}
          />
          <StatCard
            label="Nachweis fehlt oder abgelaufen"
            value={nachweisProblem.length}
            tone={nachweisProblem.length > 0 ? 'danger' : 'success'}
            emphasis={nachweisProblem.length > 0 ? 'attention' : undefined}
            icon={<IconAlert width={17} height={17} />}
            hint={nachweisProblem.length > 0 ? 'Keine Beauftragung ohne gültige Haftpflicht' : 'Alle Nachweise gültig'}
          />
          <StatCard
            label="Nachweis läuft bald ab"
            value={nachweisBald.length}
            tone={nachweisBald.length > 0 ? 'warning' : 'success'}
            icon={<IconCheck width={17} height={17} />}
            hint="innerhalb der nächsten 30 Tage"
          />
          <StatCard
            label="Ø Bewertung"
            value={bewertet.length ? `${formatNumber(bewertung, 1)} / 5` : '–'}
            tone="success"
            hint={`${bewertet.length} von ${aktive.length} bewertet`}
          />
        </div>
      )}

      {tab === 'vergaben' && (
        <>
          <Card noBody>
            <div className="card-body tight">
              <div className="row" style={{ gap: 10 }}>
                <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 340 }}>
                  <input
                    type="search"
                    placeholder="Nummer, Titel oder Dienstleister suchen …"
                    value={filters.search}
                    onChange={(event) => setFilters({ ...filters, search: event.target.value })}
                    style={{ paddingLeft: 34 }}
                  />
                  <span style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-subtle)' }}>
                    <IconSearch width={15} height={15} />
                  </span>
                </div>
                <label className="toolbar-field">
                  <span>Status</span>
                  <select
                    value={filters.status}
                    onChange={(event) => setFilters({ ...filters, status: event.target.value })}
                  >
                    <option value="">alle</option>
                    {Object.entries(STATUS).map(([key, entry]) => (
                      <option key={key} value={key}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </Card>

          {orders.loading && !orders.data ? (
            <Loading />
          ) : items.length === 0 ? (
            <Card>
              <EmptyState
                icon={<IconUsers width={20} height={20} />}
                title="Keine Vergabe gefunden"
                hint="Eine Vergabe entsteht, wenn eine Leistung nicht mit eigenen Kräften erbracht wird."
              />
            </Card>
          ) : (
            <Card noBody>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Nummer</th>
                      <th>Leistung</th>
                      <th>Dienstleister</th>
                      <th>Objekt</th>
                      <th className="num">Wert</th>
                      <th>Status</th>
                      <th className="actions">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((order) => {
                      const status = STATUS[order.status] ?? { label: order.status, variant: '' };
                      const insurance = insuranceState(order.insurance_valid_until);
                      return (
                        <tr
                          key={order.id}
                          className="clickable"
                          onClick={(event) => {
                            // Die Aktionsspalte hat eigene Knöpfe – ein Klick
                            // dort darf nicht zusätzlich das Overlay öffnen.
                            if ((event.target as HTMLElement).closest('.actions')) return;
                            setModal(`vergabe-${order.id}`);
                          }}
                        >
                          <td className="mono small">
                            {order.order_no}
                            <div className="small subtle">{formatDate(order.created_at)}</div>
                          </td>
                          <td>
                            <strong>{order.title}</strong>
                            {order.task_no && <div className="small subtle mono">zu {order.task_no}</div>}
                          </td>
                          <td className="small">
                            {order.contractor_name ?? '–'}
                            {insurance === 'expired' && (
                              <div className="small" style={{ color: 'var(--danger-text)' }}>
                                Nachweis abgelaufen
                              </div>
                            )}
                          </td>
                          <td className="small subtle">{order.property_name ?? '–'}</td>
                          <td className="num">
                            {formatCurrency(order.actual_net ?? order.estimated_net)}
                            {order.actual_net != null && order.actual_net !== order.estimated_net && (
                              <div className="small subtle">geschätzt {formatCurrency(order.estimated_net)}</div>
                            )}
                          </td>
                          <td>
                            <Badge variant={status.variant}>{status.label}</Badge>
                            {order.status === 'rejected' && order.rejection_note && (
                              <div className="small subtle truncate" style={{ maxWidth: 180 }} title={order.rejection_note}>
                                {order.rejection_note}
                              </div>
                            )}
                            {order.status === 'completed' && order.rating && (
                              <div className="small subtle">Bewertung {order.rating}/5</div>
                            )}
                          </td>
                          <td className="actions">
                            <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                              {order.status === 'pending_approval' && mayApprove && (
                                <>
                                  <button
                                    className="btn sm"
                                    onClick={() => act(order, 'approve', undefined, 'Vergabe freigegeben.')}
                                  >
                                    Freigeben
                                  </button>
                                  <button className="btn sm ghost" onClick={() => setModal(`ablehnen-${order.id}`)}>
                                    Ablehnen
                                  </button>
                                </>
                              )}
                              {order.status === 'approved' && mayCommission && (
                                <button className="btn sm primary" onClick={() => setModal(`beauftragen-${order.id}`)}>
                                  Beauftragen
                                </button>
                              )}
                              {order.status === 'commissioned' && mayCommission && (
                                <button className="btn sm" onClick={() => setModal(`abschliessen-${order.id}`)}>
                                  Abschließen
                                </button>
                              )}
                              {['commissioned', 'completed'].includes(order.status) && (
                                <button className="btn sm ghost" onClick={() => setModal(`schreiben-${order.id}`)}>
                                  Schreiben
                                </button>
                              )}
                              {['pending_approval', 'approved'].includes(order.status) && mayRequest && (
                                <button
                                  className="btn sm ghost"
                                  onClick={() => {
                                    if (window.confirm(`Vergabe ${order.order_no} stornieren?`)) {
                                      void act(order, 'cancel', undefined, 'Vergabe storniert.');
                                    }
                                  }}
                                >
                                  Stornieren
                                </button>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {tab === 'dienstleister' && (
        <>
          <Card noBody>
            <div className="card-body tight">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(event) => setShowInactive(event.target.checked)}
                />
                Stillgelegte anzeigen
              </label>
            </div>
          </Card>

          {contractors.loading && !contractors.data ? (
            <Loading />
          ) : (contractors.data ?? []).length === 0 ? (
            <Card>
              <EmptyState title="Kein Dienstleister hinterlegt" />
            </Card>
          ) : (
            <Card noBody>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Dienstleister</th>
                      <th>Gewerke</th>
                      <th className="num">Stundensatz</th>
                      <th>Haftpflicht bis</th>
                      <th>Rahmenvertrag</th>
                      <th className="num">Vergaben</th>
                      <th className="num">Bewertung</th>
                      {mayContractors && <th className="actions">Aktionen</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(contractors.data ?? []).map((contractor: any) => {
                      const insurance = insuranceState(contractor.insurance_valid_until);
                      return (
                        <tr key={contractor.id} style={contractor.is_active ? undefined : { opacity: 0.55 }}>
                          <td>
                            <strong>{contractor.name}</strong>
                            <div className="small subtle">
                              {contractor.contact_name ?? '–'}
                              {contractor.contact_phone ? ` · ${contractor.contact_phone}` : ''}
                            </div>
                          </td>
                          <td className="small subtle truncate" style={{ maxWidth: 220 }}>
                            {contractor.trades ?? '–'}
                          </td>
                          <td className="num">
                            {contractor.hourly_rate != null ? formatCurrency(contractor.hourly_rate) : '–'}
                          </td>
                          <td className="small">
                            {contractor.insurance_valid_until ? (
                              <Badge variant={insurance === 'expired' ? 'danger' : 'success'}>
                                {formatDate(contractor.insurance_valid_until)}
                              </Badge>
                            ) : (
                              <Badge variant="warning">kein Nachweis</Badge>
                            )}
                          </td>
                          <td className="small subtle">{contractor.framework_contract ?? '–'}</td>
                          <td className="num">{contractor.order_count}</td>
                          <td className="num">
                            {contractor.avg_rating != null ? `${contractor.avg_rating} / 5` : '–'}
                          </td>
                          {mayContractors && (
                            <td className="actions">
                              {contractor.is_active ? (
                                <RowActions
                                  label={contractor.name}
                                  onEdit={() => setModal(`dienstleister-${contractor.id}`)}
                                  deletePath={`/api/contracting/contractors/${contractor.id}${
                                    contractor.order_count > 0 ? '' : '?permanent=true'
                                  }`}
                                  confirmText={
                                    contractor.order_count > 0
                                      ? `An „${contractor.name}“ hängen ${contractor.order_count} Vergabe(n). Der Eintrag wird stillgelegt und bleibt als Beleg erhalten. Fortfahren?`
                                      : `Dienstleister „${contractor.name}“ löschen?`
                                  }
                                  onDeleted={() => contractors.reload()}
                                />
                              ) : (
                                <button
                                  className="btn sm"
                                  onClick={async () => {
                                    await api.post(`/api/contracting/contractors/${contractor.id}/reactivate`);
                                    notify('Dienstleister wieder aktiv.', 'success');
                                    contractors.reload();
                                  }}
                                >
                                  Reaktivieren
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {modal === 'neu' && (
        <OrderModal
          contractors={(contractors.data ?? []).filter((entry: any) => entry.is_active)}
          threshold={threshold}
          onClose={() => setModal(null)}
          onSaved={(result) => {
            setModal(null);
            orders.reload();
            notify(
              result.status === 'pending_approval'
                ? `${result.orderNo} angelegt und zur Freigabe vorgelegt.`
                : `${result.orderNo} angelegt und ohne Freigabe erteilt – unter der Wertgrenze.`,
              'success',
            );
          }}
        />
      )}

      {modal?.startsWith('vergabe-') && (
        <OrderModal
          contractors={contractors.data ?? []}
          threshold={threshold}
          order={items.find((entry) => String(entry.id) === modal.replace('vergabe-', ''))}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            orders.reload();
            notify('Vergabe gespeichert.', 'success');
          }}
        />
      )}

      {modal?.startsWith('ablehnen-') && (
        <RejectModal
          order={items.find((entry) => String(entry.id) === modal.replace('ablehnen-', ''))}
          onClose={() => setModal(null)}
          onSubmit={(order, note) => act(order, 'reject', { note }, 'Vergabe abgelehnt.')}
        />
      )}

      {modal?.startsWith('beauftragen-') && (
        <CommissionModal
          order={items.find((entry) => String(entry.id) === modal.replace('beauftragen-', ''))}
          onClose={() => setModal(null)}
          onSubmit={(order, force) => act(order, 'commission', { force }, 'Dienstleister beauftragt.')}
        />
      )}

      {modal?.startsWith('abschliessen-') && (
        <CompleteModal
          order={items.find((entry) => String(entry.id) === modal.replace('abschliessen-', ''))}
          onClose={() => setModal(null)}
          onSubmit={(order, payload) => act(order, 'complete', payload, 'Vergabe abgeschlossen.')}
        />
      )}

      {modal?.startsWith('schreiben-') && (
        <DocumentsModal
          order={items.find((entry) => String(entry.id) === modal.replace('schreiben-', ''))}
          mayResend={mayCommission}
          onClose={() => setModal(null)}
        />
      )}

      {(modal === 'dienstleister-neu' || modal?.startsWith('dienstleister-')) && modal !== 'dienstleister-neu' && (
        <ContractorModal
          contractor={(contractors.data ?? []).find(
            (entry: any) => String(entry.id) === modal.replace('dienstleister-', ''),
          )}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            contractors.reload();
            notify('Dienstleister gespeichert.', 'success');
          }}
        />
      )}

      {modal === 'dienstleister-neu' && (
        <ContractorModal
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            contractors.reload();
            notify('Dienstleister angelegt.', 'success');
          }}
        />
      )}
    </div>
  );
}

/**
 * Vergabe anlegen, ansehen oder ändern.
 *
 * Ein Formular für alle drei Fälle – dasselbe Vorgehen wie beim
 * `ContractorModal` weiter unten. Änderbar ist eine Vergabe nur bis zur
 * Freigabe; danach zeigt dasselbe Fenster die Angaben schreibgeschützt, denn
 * ein nachträglich geänderter Betrag entwertete die erteilte Freigabe.
 */
function OrderModal({
  contractors,
  threshold,
  order,
  onClose,
  onSaved,
}: {
  contractors: any[];
  threshold: number;
  order?: any;
  onClose: () => void;
  onSaved: (result: any) => void;
}) {
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);
  const tasks = useAsync(() => api.get('/api/tasks', { open: 'true', limit: 100 }).catch(() => null), []);
  const [form, setForm] = useState({
    contractorId: order?.contractor_id ? String(order.contractor_id) : '',
    taskId: order?.task_id ? String(order.task_id) : '',
    propertyId: order?.property_id ? String(order.property_id) : '',
    title: order?.title ?? '',
    description: order?.description ?? '',
    estimatedNet: order?.estimated_net != null ? String(order.estimated_net) : '',
    scheduledFor: order?.scheduled_for?.slice(0, 10) ?? '',
    note: order?.note ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const gewaehlt = contractors.find((entry) => String(entry.id) === form.contractorId);
  const insurance = gewaehlt ? insuranceState(gewaehlt.insurance_valid_until) : 'unknown';
  const wert = Number(form.estimatedNet || 0);
  const braucht = wert >= threshold;
  const aenderbar = !order || ['draft', 'pending_approval'].includes(order.status);

  return (
    <Modal
      title={order ? `Vergabe ${order.order_no}` : 'Auftragsvergabe beantragen'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <span className="small subtle" style={{ marginRight: 'auto' }}>
            {!aenderbar
              ? 'Nach der Freigabe nicht mehr änderbar – die Angaben stehen für den erteilten Auftrag.'
              : form.estimatedNet
                ? braucht
                  ? `Wert ab ${formatCurrency(threshold)} – geht zur Freigabe.`
                  : `Unter ${formatCurrency(threshold)} – wird ohne Freigabe erteilt.`
                : `Freigabe ab ${formatCurrency(threshold)}`}
          </span>
          <button className="btn" onClick={onClose}>
            {aenderbar ? 'Abbrechen' : 'Schließen'}
          </button>
          {aenderbar && (
            <button
              className="btn primary"
              disabled={!form.contractorId || form.title.trim().length < 3 || !form.estimatedNet}
              onClick={async () => {
                try {
                  const payload = {
                    contractorId: Number(form.contractorId),
                    taskId: form.taskId ? Number(form.taskId) : null,
                    propertyId: form.propertyId ? Number(form.propertyId) : null,
                    title: form.title,
                    description: form.description || null,
                    estimatedNet: wert,
                    scheduledFor: form.scheduledFor || null,
                    note: form.note || null,
                  };
                  const result = order
                    ? await api.patch(`/api/contracting/orders/${order.id}`, payload)
                    : await api.post('/api/contracting/orders', payload);
                  onSaved(result);
                } catch (caught) {
                  setError(caught);
                }
              }}
            >
              {order ? 'Speichern' : braucht ? 'Zur Freigabe vorlegen' : 'Anlegen'}
            </button>
          )}
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        {insurance === 'expired' && (
          <div className="alert warning">
            <IconAlert width={16} height={16} />
            <span>
              Der Versicherungsnachweis von „{gewaehlt.name}“ ist am {formatDate(gewaehlt.insurance_valid_until)}{' '}
              abgelaufen. Der Antrag lässt sich stellen, die Beauftragung wird aber blockiert, bis ein neuer Nachweis
              vorliegt.
            </span>
          </div>
        )}

        <div className="form-grid">
          <Field label="Dienstleister" required>
            <SearchSelect
              value={String(form.contractorId ?? '')}
              onChange={(wert) => set('contractorId', wert)}
              options={(contractors ?? []).map((entry: any) => ({ value: String(entry.id), label: `${entry.name} ${entry.trades ? ` · ${entry.trades.split(',')[0].trim()}` : ''}` }))}
              placeholder="– bitte wählen –"
              disabled={!aenderbar}
            />
          </Field>
          <Field label="Geschätzter Nettowert (€)" required>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.estimatedNet}
              disabled={!aenderbar}
              onChange={(event) => set('estimatedNet', event.target.value)}
            />
          </Field>
        </div>

        <Field label="Leistung" required>
          <input
            value={form.title}
            autoFocus
            disabled={!aenderbar}
            onChange={(event) => set('title', event.target.value)}
            placeholder="z. B. Kanalreinigung mit Kamerabefahrung"
          />
        </Field>
        <Field label="Beschreibung" hint="Was genau vergeben wird – Grundlage für die Freigabeentscheidung.">
          <textarea
            rows={3}
            value={form.description}
            disabled={!aenderbar}
            onChange={(event) => set('description', event.target.value)}
          />
        </Field>

        <div className="form-grid">
          <Field label="Zu Auftrag" hint="Der Grund der Vergabe steht meist dort.">
            <SearchSelect
              value={String(form.taskId ?? '')}
              onChange={(wert) => set('taskId', wert)}
              options={(tasks.data?.items ?? []).map((task: any) => ({ value: String(task.id), label: `${task.task_no} · ${task.title}` }))}
              placeholder="Ohne Auftragsbezug"
              disabled={!aenderbar}
            />
          </Field>
          <Field label="Objekt" hint="Wird aus dem Auftrag übernommen, wenn dort hinterlegt.">
            <SearchSelect
              value={String(form.propertyId ?? '')}
              onChange={(wert) => set('propertyId', wert)}
              options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
              placeholder="– keines –"
              disabled={!aenderbar}
            />
          </Field>
          <Field label="Ausführung geplant für">
            <input
              type="date"
              value={form.scheduledFor}
              disabled={!aenderbar}
              onChange={(event) => set('scheduledFor', event.target.value)}
            />
          </Field>
          <Field label="Bemerkung">
            <input value={form.note} disabled={!aenderbar} onChange={(event) => set('note', event.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({
  order,
  onClose,
  onSubmit,
}: {
  order: any;
  onClose: () => void;
  onSubmit: (order: any, note: string) => void;
}) {
  const [note, setNote] = useState('');
  if (!order) return null;
  return (
    <Modal
      title={`Vergabe ablehnen – ${order.order_no}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn danger" disabled={note.trim().length < 3} onClick={() => onSubmit(order, note)}>
            Ablehnen
          </button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="Begründung"
          required
          hint="Sie geht an die antragstellende Person – ohne sie beginnt dieselbe Vergabe morgen erneut."
        >
          <textarea rows={3} value={note} autoFocus onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function CommissionModal({
  order,
  onClose,
  onSubmit,
}: {
  order: any;
  onClose: () => void;
  onSubmit: (order: any, force: boolean) => void;
}) {
  const [force, setForce] = useState(false);
  if (!order) return null;
  const insurance = insuranceState(order.insurance_valid_until);

  return (
    <Modal
      title={`Beauftragen – ${order.order_no}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={insurance === 'expired' && !force}
            onClick={() => onSubmit(order, force)}
          >
            Verbindlich beauftragen
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">Dienstleister</span>
            <span className="detail-value">{order.contractor_name}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Leistung</span>
            <span className="detail-value">{order.title}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Wert</span>
            <span className="detail-value">{formatCurrency(order.estimated_net)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Freigegeben von</span>
            <span className="detail-value">
              {order.approved_first_name ? `${order.approved_first_name} ${order.approved_last_name}` : '–'}
              {order.approved_at && <span className="small subtle"> · {formatDateTime(order.approved_at)}</span>}
            </span>
          </div>
        </div>

        {insurance === 'expired' ? (
          <div className="alert danger">
            <IconAlert width={16} height={16} />
            <span>
              Der Versicherungsnachweis ist am {formatDate(order.insurance_valid_until)} abgelaufen. Bei einer Leistung
              auf fremdem Grundstück haftet im Schadensfall sonst der Auftraggeber mit.
            </span>
          </div>
        ) : insurance === 'unknown' ? (
          <div className="alert warning">
            Für diesen Dienstleister ist kein Versicherungsnachweis hinterlegt.
          </div>
        ) : (
          <div className="note-box tone-success">
            <IconCheck width={15} height={15} />
            <span>Versicherungsnachweis gültig bis {formatDate(order.insurance_valid_until)}.</span>
          </div>
        )}

        {insurance === 'expired' && (
          <Toggle
            checked={force}
            onChange={setForce}
            label="Ich beauftrage trotz abgelaufenem Nachweis – der Vorgang wird so protokolliert."
          />
        )}
      </div>
    </Modal>
  );
}

function CompleteModal({
  order,
  onClose,
  onSubmit,
}: {
  order: any;
  onClose: () => void;
  onSubmit: (order: any, payload: any) => void;
}) {
  const [actualNet, setActualNet] = useState(String(order?.estimated_net ?? ''));
  const [rating, setRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  if (!order) return null;

  const abweichung = Number(actualNet || 0) - Number(order.estimated_net ?? 0);

  return (
    <Modal
      title={`Abschließen – ${order.order_no}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            onClick={() =>
              onSubmit(order, {
                actualNet: actualNet ? Number(actualNet) : null,
                rating: rating || null,
                ratingNote: ratingNote || null,
              })
            }
          >
            Abschließen
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Tatsächlicher Nettobetrag (€)" hint={`Geschätzt waren ${formatCurrency(order.estimated_net)}.`}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={actualNet}
            autoFocus
            onChange={(event) => setActualNet(event.target.value)}
          />
        </Field>

        {Math.abs(abweichung) >= 0.01 && (
          <div className={`alert ${Math.abs(abweichung) / (order.estimated_net || 1) > 0.2 ? 'warning' : 'info'}`}>
            Abweichung {abweichung > 0 ? '+' : ''}
            {formatCurrency(abweichung)} gegenüber der Schätzung.
          </div>
        )}

        {/* Die Bewertung ist die einzige Rückkopplung, die der nächsten
            Vergabeentscheidung zur Verfügung steht. */}
        <Field label="Bewertung der Leistung" hint="Fließt in den Durchschnitt am Dienstleister ein.">
          <div className="row" style={{ gap: 6 }}>
            {[1, 2, 3, 4, 5].map((wert) => (
              <button
                key={wert}
                className={`btn sm ${rating === wert ? 'primary' : ''}`}
                onClick={() => setRating(rating === wert ? 0 : wert)}
              >
                {wert}
              </button>
            ))}
            <span className="small subtle">{rating ? `${rating} von 5` : 'ohne Bewertung'}</span>
          </div>
        </Field>
        <Field label="Anmerkung zur Leistung">
          <input value={ratingNote} onChange={(event) => setRatingNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ContractorModal({
  contractor,
  onClose,
  onSaved,
}: {
  contractor?: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: contractor?.name ?? '',
    trades: contractor?.trades ?? '',
    contactName: contractor?.contact_name ?? '',
    contactEmail: contractor?.contact_email ?? '',
    contactPhone: contractor?.contact_phone ?? '',
    street: contractor?.street ?? '',
    zip: contractor?.zip ?? '',
    city: contractor?.city ?? '',
    hourlyRate: contractor?.hourly_rate != null ? String(contractor.hourly_rate) : '',
    insuranceValidUntil: contractor?.insurance_valid_until ?? '',
    frameworkContract: contractor?.framework_contract ?? '',
    note: contractor?.note ?? '',
    isActive: contractor ? !!contractor.is_active : true,
  });
  const [error, setError] = useState<unknown>(null);
  const set = (key: keyof typeof form, value: any) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal
      title={contractor ? `Dienstleister bearbeiten – ${contractor.name}` : 'Dienstleister anlegen'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={form.name.trim().length < 2}
            onClick={async () => {
              try {
                const payload = {
                  ...form,
                  hourlyRate: form.hourlyRate ? Number(form.hourlyRate) : null,
                  trades: form.trades || null,
                  contactName: form.contactName || null,
                  contactEmail: form.contactEmail || null,
                  contactPhone: form.contactPhone || null,
                  street: form.street || null,
                  zip: form.zip || null,
                  city: form.city || null,
                  insuranceValidUntil: form.insuranceValidUntil || null,
                  frameworkContract: form.frameworkContract || null,
                  note: form.note || null,
                };
                if (contractor) await api.patch(`/api/contracting/contractors/${contractor.id}`, payload);
                else await api.post('/api/contracting/contractors', payload);
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Firma" required>
            <input value={form.name} autoFocus onChange={(event) => set('name', event.target.value)} />
          </Field>
          <Field label="Gewerke" hint="Kommagetrennt – auch Mischgewerke, die im eigenen Katalog nicht vorkommen.">
            <input
              value={form.trades}
              onChange={(event) => set('trades', event.target.value)}
              placeholder="Sanitär, Kanalreinigung"
            />
          </Field>
          <Field label="Ansprechpartner">
            <input value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          </Field>
          <Field label="Telefon">
            <input value={form.contactPhone} onChange={(event) => set('contactPhone', event.target.value)} />
          </Field>
          <Field label="E-Mail">
            <input type="email" value={form.contactEmail} onChange={(event) => set('contactEmail', event.target.value)} />
          </Field>
          <Field label="Stundensatz (€)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.hourlyRate}
              onChange={(event) => set('hourlyRate', event.target.value)}
            />
          </Field>
          <Field
            label="Haftpflicht gültig bis"
            hint="Ohne gültigen Nachweis wird die Beauftragung angehalten."
          >
            <input
              type="date"
              value={form.insuranceValidUntil}
              onChange={(event) => set('insuranceValidUntil', event.target.value)}
            />
          </Field>
          <Field label="Rahmenvertrag">
            <input
              value={form.frameworkContract}
              onChange={(event) => set('frameworkContract', event.target.value)}
              placeholder="RV-2025-014"
            />
          </Field>
          <Field label="Straße">
            <input value={form.street} onChange={(event) => set('street', event.target.value)} />
          </Field>
          <Field label="PLZ">
            <input value={form.zip} onChange={(event) => set('zip', event.target.value)} />
          </Field>
          <Field label="Ort">
            <input value={form.city} onChange={(event) => set('city', event.target.value)} />
          </Field>
        </div>
        <Field label="Bemerkung">
          <input value={form.note} onChange={(event) => set('note', event.target.value)} />
        </Field>
        <Toggle checked={form.isActive} onChange={(value) => set('isActive', value)} label="Aktiv" />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Beauftragungsschreiben                                              */
/* ------------------------------------------------------------------ */

const MAIL_STATUS: Record<string, { label: string; variant: string }> = {
  pending: { label: 'im Postausgang', variant: 'warning' },
  sent: { label: 'versendet', variant: 'success' },
  failed: { label: 'gescheitert', variant: 'danger' },
};

/**
 * Die zu einer Vergabe erzeugten Schreiben.
 *
 * Zeigt neben der Datei den Versandstand: Ein Schreiben, das im Postausgang
 * hängt, weil kein Mailserver eingerichtet ist, sieht sonst genauso aus wie
 * eines, das beim Dienstleister angekommen ist.
 */
function DocumentsModal({
  order,
  mayResend,
  onClose,
}: {
  order: any;
  mayResend: boolean;
  onClose: () => void;
}) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);
  const documents = useAsync(
    () => (order ? api.get(`/api/contracting/orders/${order.id}/documents`) : Promise.resolve([])),
    [order?.id],
  );

  if (!order) return null;

  const oeffnen = async (documentId: number, filename: string) => {
    const response = await request<Response>(`/api/contracting/documents/${documentId}`, { raw: true });
    if (!response.ok) {
      notify('Das Schreiben konnte nicht geladen werden.', 'error');
      return;
    }
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const erneutSenden = async () => {
    setBusy(true);
    try {
      const ergebnis: any = await api.post(`/api/contracting/orders/${order.id}/resend`, {});
      notify(
        ergebnis?.mailId
          ? `Beauftragung an ${ergebnis.recipients.join(', ')} versendet.`
          : `Schreiben erzeugt, aber nicht versendet: ${ergebnis?.reason ?? 'unbekannt'}`,
        ergebnis?.mailId ? 'success' : 'info',
      );
      documents.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Versand nicht möglich.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const liste: any[] = documents.data ?? [];

  return (
    <Modal
      title={`Beauftragung ${order.order_no}`}
      onClose={onClose}
      size="wide"
      footer={
        <div className="row" style={{ justifyContent: 'space-between', width: '100%' }}>
          <span className="small subtle">
            {order.contractor_name}
            {order.contractor_email ? ` · ${order.contractor_email}` : ' · keine E-Mail-Adresse hinterlegt'}
          </span>
          <span className="row" style={{ gap: 8 }}>
            <button className="btn ghost" onClick={onClose}>
              Schließen
            </button>
            {mayResend && (
              <button className="btn primary" disabled={busy} onClick={erneutSenden}>
                {busy ? 'Wird gesendet …' : 'Erneut senden'}
              </button>
            )}
          </span>
        </div>
      }
    >
      {documents.loading && <Loading />}
      {documents.error ? <ErrorMessage error={documents.error} /> : null}

      {!documents.loading && liste.length === 0 && (
        <EmptyState
          title="Noch kein Schreiben erzeugt"
          hint="Beim Beauftragen entsteht das Schreiben automatisch. Über „Erneut senden“ lässt es sich nachträglich erzeugen."
        />
      )}

      {liste.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Datei</th>
              <th>Erzeugt</th>
              <th>Empfänger</th>
              <th>Versand</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liste.map((doc) => {
              const empfaenger: string[] = doc.recipients ? JSON.parse(doc.recipients) : [];
              const stand = doc.mail_id ? (MAIL_STATUS[doc.mail_status] ?? null) : null;
              return (
                <tr key={doc.id}>
                  <td>
                    <div className="truncate" style={{ fontWeight: 600, maxWidth: 260 }}>
                      {doc.filename}
                    </div>
                    <div className="small subtle">{Math.round(doc.size_bytes / 1024)} KB</div>
                  </td>
                  <td className="small">
                    {formatDateTime(doc.created_at)}
                    {doc.first_name && (
                      <div className="subtle">
                        {doc.first_name} {doc.last_name}
                      </div>
                    )}
                  </td>
                  <td className="small">{empfaenger.length ? empfaenger.join(', ') : <span className="subtle">–</span>}</td>
                  <td>
                    {stand ? (
                      <>
                        <Badge variant={stand.variant} dot>
                          {stand.label}
                        </Badge>
                        {doc.mail_sent_at && <div className="small subtle">{formatDateTime(doc.mail_sent_at)}</div>}
                        {doc.mail_error && (
                          <div className="small" style={{ color: 'var(--danger-text)' }} title={doc.mail_error}>
                            {String(doc.mail_error).slice(0, 60)}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="small subtle">nicht versendet</span>
                    )}
                  </td>
                  <td className="actions">
                    <button className="btn sm ghost" onClick={() => oeffnen(doc.id, doc.filename)}>
                      Herunterladen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
